import React, { useState, useRef, useEffect, useCallback } from 'react';
import html2canvas from 'html2canvas';
import './App.css';
import { FONTS } from './constants';
import { AlignLeftIcon, AlignCenterIcon, AlignRightIcon } from './icons';

// Custom Hook for Image Upload
const useImageUpload = () => {
  const [image, setImage] = useState(null);
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImage(URL.createObjectURL(file));
    }
  };
  return { image, handleImageUpload, setImage };
};

// Custom Hook for Text Editing
const useTextEditing = (initialText = 'Sample Text') => {
  const [text, setText] = useState(initialText);
  const [fontSize, setFontSize] = useState(2.0); // Default to 2.0em
  const [lineHeight, setLineHeight] = useState(1.0); // Default to 1.0
  const [fontFamily, setFontFamily] = useState('Oswald');
  const [textAlign, setTextAlign] = useState('center'); // Default to center
  const [textBackgroundColor, setTextBackgroundColor] = useState('#000000'); // Default to black
  const [textBackgroundOpacity, setTextBackgroundOpacity] = useState(0.7); // Default to 0.7

  const handleTextChange = (event) => setText(event.target.value);

  const handlePaste = useCallback(async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      setText(clipboardText);
    } catch (err) {
      console.error('Failed to read clipboard contents: ', err);
      alert('Failed to read clipboard. Please ensure you have granted permission.');
    }
  }, []);

  const handleDeleteText = () => {
    if (window.confirm('Are you sure you want to delete the text overlay?')) {
      setText('');
    }
  };

  return {
    text, setText,
    fontSize, setFontSize,
    lineHeight, setLineHeight,
    fontFamily, setFontFamily,
    textAlign, setTextAlign,
    textBackgroundColor, setTextBackgroundColor,
    textBackgroundOpacity, setTextBackgroundOpacity,
    handleTextChange, handlePaste, handleDeleteText
  };
};

// Custom Hook for Drag functionality
const useDrag = (initialPosition = { x: 20, y: 20 }, imagePreviewRef, textOverlayRef) => {
  const [position, setPosition] = useState(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [showVerticalCenterGuide, setShowVerticalCenterGuide] = useState(false); // New state for vertical center guide

  const handleDragStart = useCallback((e) => {
    if (!textOverlayRef.current) return;
    const textRect = textOverlayRef.current.getBoundingClientRect();
    let clientX, clientY;
    if (e.touches) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    setDragOffset({
      x: clientX - textRect.left,
      y: clientY - textRect.top,
    });
    setIsDragging(true);
    e.preventDefault();
  }, [textOverlayRef]);

  const handleDragMove = useCallback((e) => {
    if (isDragging) {
      const imageRect = imagePreviewRef.current.getBoundingClientRect();
      let clientX, clientY;
      if (e.touches) {
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
        e.preventDefault();
      } else {
        clientX = e.clientX;
        clientY = e.clientY;
      }

      const newX_px = clientX - imageRect.left - dragOffset.x;
      const newY_px = clientY - imageRect.top - dragOffset.y;
      
      const textWidth = textOverlayRef.current.offsetWidth;
      const textHeight = textOverlayRef.current.offsetHeight;

      // Ensure the text stays within the image boundaries, with a small padding
      const constrainedX_px = Math.max(imageRect.width * 0.03, Math.min(newX_px, imageRect.width * 0.97 - textWidth));
      const constrainedY_px = Math.max(0, Math.min(newY_px, imageRect.height - textHeight));

      setPosition({
        x: (constrainedX_px / imageRect.width) * 100,
        y: (constrainedY_px / imageRect.height) * 100,
      });

      // Calculate if horizontally centered
      const textCenterX = constrainedX_px + (textWidth / 2);
      const imageCenterX = imageRect.width / 2;
      const tolerance = 5; // Pixels
      setShowVerticalCenterGuide(Math.abs(textCenterX - imageCenterX) < tolerance);

    }
  }, [isDragging, dragOffset, imagePreviewRef, textOverlayRef]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    setShowVerticalCenterGuide(false); // Hide guide when dragging ends
  }, []);

  return { position, handleDragStart, handleDragMove, handleDragEnd, isDragging, showVerticalCenterGuide };
};

// Custom Hook for Facebook Guide
const useFacebookGuide = (image, imagePreviewRef) => {
  const [showFacebookGuide, setShowFacebookGuide] = useState(true);
  const [facebookGuideStyles, setFacebookGuideStyles] = useState({});

  useEffect(() => {
    const calculateGuideDimensions = () => {
      if (imagePreviewRef.current && image) {
        const imgElement = imagePreviewRef.current.querySelector('img');
        if (imgElement && imgElement.offsetWidth > 0 && imgElement.offsetHeight > 0) {
          const imgWidth = imgElement.getBoundingClientRect().width;
          const imgHeight = imgElement.getBoundingClientRect().height;

          const aspectRatio = 4 / 5; // Width / Height

          let guideWidth, guideHeight;

          const potentialGuideHeightFromWidth = imgWidth / aspectRatio;

          if (potentialGuideHeightFromWidth <= imgHeight) {
            guideWidth = imgWidth;
            guideHeight = potentialGuideHeightFromWidth;
          } else {
            guideHeight = imgHeight;
            guideWidth = imgHeight * aspectRatio;
          }

          setFacebookGuideStyles({
            width: `${guideWidth}px`,
            height: `${guideHeight}px`,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            position: 'absolute',
            border: '3px dashed rgba(255, 255, 255, 0.8)',
            boxSizing: 'border-box',
            pointerEvents: 'none',
            boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.5), 0 0 10px rgba(255, 255, 255, 0.5)',
          });
        }
      } else {
        setFacebookGuideStyles({});
      }
    };

    const imgElement = imagePreviewRef.current?.querySelector('img');
    if (imgElement) {
      imgElement.addEventListener('load', calculateGuideDimensions);
    }
    window.addEventListener('resize', calculateGuideDimensions);
    calculateGuideDimensions();

    return () => {
      if (imgElement) {
        imgElement.removeEventListener('load', calculateGuideDimensions);
      }
      window.removeEventListener('resize', calculateGuideDimensions);
    };
  }, [image, imagePreviewRef]);

  return { showFacebookGuide, setShowFacebookGuide, facebookGuideStyles };
};


function App() {
  const imagePreviewRef = useRef(null);
  const textOverlayRef = useRef(null);

  const { image, handleImageUpload, setImage } = useImageUpload();
  const {
    text, setText, fontSize, setFontSize, lineHeight, setLineHeight,
    fontFamily, setFontFamily, textAlign, setTextAlign,
    textBackgroundColor, setTextBackgroundColor,
    textBackgroundOpacity, setTextBackgroundOpacity,
    handleTextChange, handlePaste, handleDeleteText
  } = useTextEditing();
  const { position, handleDragStart, handleDragMove, handleDragEnd, isDragging, showVerticalCenterGuide } = useDrag({ x: 20, y: 20 }, imagePreviewRef, textOverlayRef);
  const { showFacebookGuide, setShowFacebookGuide, facebookGuideStyles } = useFacebookGuide(image, imagePreviewRef);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState('');

  const handleGeneratePreview = () => {
    if (!imagePreviewRef.current) return;

    setShowFacebookGuide(false);

    setTimeout(() => {
      html2canvas(imagePreviewRef.current, { useCORS: true, allowTaint: true }).then(canvas => {
        setGeneratedImageUrl(canvas.toDataURL('image/png'));
        setIsModalOpen(true);
      }).catch(error => {
        console.error("Error generating image:", error);
        alert("Could not generate image. Please try again or check browser console for errors.");
      }).finally(() => {
        setShowFacebookGuide(true);
      });
    }, 0);
  };

  const hexToRgba = (hex, opacity) => {
    let r = 0, g = 0, b = 0;
    // 3 digits
    if (hex.length === 4) {
      r = "0x" + hex[1] + hex[1];
      g = "0x" + hex[2] + hex[2];
      b = "0x" + hex[3] + hex[3];
    // 6 digits
    } else if (hex.length === 7) {
      r = "0x" + hex[1] + hex[2];
      g = "0x" + hex[3] + hex[4];
      b = "0x" + hex[5] + hex[6];
    }
    return `rgba(${+r},${+g},${+b},${opacity})`;
  };

  return (
    <div className="app-container">
      <h1>Image Text Overlay Editor</h1>
      <div className="main-content">
        <div className="controls">
          <h2>Controls</h2>
          <div className="control-group">
            <label htmlFor="imageUpload">Upload Image</label>
            <input id="imageUpload" type="file" accept="image/*" onChange={handleImageUpload} />
          </div>
          {image && (
            <>
              <div className="control-group">
                <label htmlFor="textInput">Text</label>
                <textarea id="textInput" rows="3" value={text} onChange={handleTextChange} />
                <button onClick={handlePaste} className="paste-button">Paste</button>
              </div>
              <div className="control-group">
                <label htmlFor="fontFamilySelect">Font Family</label>
                <select id="fontFamilySelect" value={fontFamily} onChange={(e) => setFontFamily(e.target.value)}>
                  {FONTS.map(font => <option key={font} value={font}>{font}</option>)}
                </select>
              </div>
              <div className="control-group">
                <label>Text Align</label>
                <div className="alignment-controls">
                  <button className={`align-btn ${textAlign === 'left' ? 'active' : ''}`} onClick={() => setTextAlign('left')}><AlignLeftIcon /></button>
                  <button className={`align-btn ${textAlign === 'center' ? 'active' : ''}`} onClick={() => setTextAlign('center')}><AlignCenterIcon /></button>
                  <button className={`align-btn ${textAlign === 'right' ? 'active' : ''}`} onClick={() => setTextAlign('right')}><AlignRightIcon /></button>
                </div>
              </div>
              <div className="control-group">
                <label htmlFor="fontSizeSlider">Font Size</label>
                <div className="slider-with-value">
                  <input id="fontSizeSlider" type="range" min="0.5" max="5.0" step="0.1" value={fontSize} onChange={e => setFontSize(parseFloat(e.target.value))} />
                  <span>{fontSize.toFixed(1)}em</span>
                </div>
              </div>
              <div className="control-group">
                <label htmlFor="lineHeightSlider">Line Spacing</label>
                <div className="slider-with-value">
                  <input id="lineHeightSlider" type="range" min="0.8" max="3.0" step="0.1" value={lineHeight} onChange={e => setLineHeight(parseFloat(e.target.value))} />
                  <span>{lineHeight.toFixed(1)}</span>
                </div>
              </div>
              <div className="control-group">
                <label htmlFor="textBackgroundColorInput">Background Color</label>
                <input
                  id="textBackgroundColorInput"
                  type="color"
                  value={textBackgroundColor}
                  onChange={e => setTextBackgroundColor(e.target.value)}
                />
              </div>
              <div className="control-group">
                <label htmlFor="textBackgroundOpacitySlider">Background Opacity</label>
                <div className="slider-with-value">
                  <input id="textBackgroundOpacitySlider" type="range" min="0" max="1" step="0.1" value={textBackgroundOpacity} onChange={e => setTextBackgroundOpacity(parseFloat(e.target.value))} />
                  <span>{textBackgroundOpacity.toFixed(1)}</span>
                </div>
              </div>
              <div className="control-group">
                <button onClick={handleDeleteText} className="delete-button">Delete Text</button>
              </div>
              <div className="control-group">
                <button onClick={handleGeneratePreview} className="download-button">Download</button>
              </div>
            </>
          )}
        </div>
        <div className="image-preview-container">
          {image ? (
            <div
              ref={imagePreviewRef}
              className="image-preview"
              onMouseMove={handleDragMove}
              onMouseUp={handleDragEnd}
              onTouchMove={handleDragMove}
              onTouchEnd={handleDragEnd}
            >
              <img src={image} alt="Preview" crossOrigin="anonymous" />
              {showFacebookGuide && <div className="facebook-guide" style={facebookGuideStyles}></div>}
              {showVerticalCenterGuide && (
                <div
                  className="vertical-center-guide"
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: 0,
                    height: '100%',
                    width: '2px',
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    transform: 'translateX(-50%)',
                    pointerEvents: 'none',
                    zIndex: 10,
                  }}
                ></div>
              )}
              {text && (
                <div
                  ref={textOverlayRef}
                  className="text-overlay"
                  style={{
                    fontSize: `${fontSize}em`,
                    fontFamily: fontFamily,
                    lineHeight: lineHeight,
                    textAlign: textAlign,
                    top: `${position.y}%`,
                    left: `${position.x}%`,
                    cursor: isDragging ? 'grabbing' : 'grab',
                    touchAction: 'none',
                    backgroundColor: hexToRgba(textBackgroundColor, textBackgroundOpacity),
                  }}
                  onMouseDown={handleDragStart}
                  onTouchStart={handleDragStart}
                >
                  {text}
                </div>
              )}
            </div>
          ) : (
            <div className="image-placeholder">Please upload an image to start</div>
          )}
        </div>
      </div>
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="modal-close-button" onClick={() => setIsModalOpen(false)}>×</button>
            <p>Right-click (desktop) or long-press (mobile) to save image</p>
            <img src={generatedImageUrl} alt="Generated preview" style={{ maxWidth: '100%' }} />
          </div>
        </div>
      )}
    </div>
  );
}

export default App;